import React from "react";
import { Link } from "react-router-dom";
import { screens } from "../../../../config/routes";
import LandItem from "../../../../components/landItem";
import LandItemSkeleton from "../../../../components/landItemSkeleton";
import { useFeaturedLands } from "../../../../shared/hooks/useFeaturedLands";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const FeaturedLands: React.FC = () => {
  const { featuredLands, loading, error } = useFeaturedLands();

  return (
    <div className="px-[6.5%] py-10 relative z-[2] bg-white">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-4">
          <h2 className="text-[18px] font-[400] md:text-[28px] lg:text-[32px] uppercase font-title italic">
            Land for sale
          </h2>
          <div className="flex items-center gap-2">
            <button className="featured-lands-prev flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-black hover:border-primary transition-all duration-300 opacity-90 hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="w-12 h-12 rounded-full border-[0.5px] border-black flex items-center justify-center">
                <ArrowLeft className="w-8 h-8 stroke-[1.5]" />
              </div>
            </button>
            <button className="featured-lands-next flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-black hover:border-primary transition-all duration-300 opacity-90 hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="w-12 h-12 rounded-full border-[0.5px] border-black flex items-center justify-center">
                <ArrowRight className="w-8 h-8 stroke-[1.5]" />
              </div>
            </button>
          </div>
        </div>
        <Link
          to={screens.lands}
          className="group relative text-black font-medium transition-colors font-title italic"
        >
          View All
          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
        </Link>
      </div>
      <hr />

      {error && <div className="text-red-500 my-4 text-center">{error}</div>}

      <div className="mt-4">
        {loading ? (
          // Show skeleton loaders while loading
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <LandItemSkeleton key={index} />
            ))}
          </div>
        ) : featuredLands.length > 0 ? (
          // Show featured lands in Swiper carousel
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={16}
            slidesPerView={1}
            navigation={{
              prevEl: ".featured-lands-prev",
              nextEl: ".featured-lands-next",
            }}
            pagination={{
              clickable: true,
              el: ".featured-lands-pagination",
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="py-4"
          >
            {featuredLands.map((land) => (
              <SwiperSlide key={land.id}>
                <LandItem
                  id={land.id}
                  title={land.title}
                  price={parseFloat(land.price)}
                  imageUrl={land.main_image_url}
                  area={parseFloat(land.area.value)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          // Show message if no featured lands
          <div className="text-center py-8 text-gray-500">
            No featured lands available at the moment.
          </div>
        )}

        {/* Pagination dots */}
        <div className="featured-lands-pagination flex justify-center gap-1 mt-8 [&_.swiper-pagination-bullet]:w-2.5 [&_.swiper-pagination-bullet]:h-2.5 [&_.swiper-pagination-bullet]:bg-gray-400 [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:transition-all [&_.swiper-pagination-bullet]:duration-300 [&_.swiper-pagination-bullet-active]:!bg-primary [&_.swiper-pagination-bullet-active]:scale-110"></div>
      </div>
    </div>
  );
};

export default FeaturedLands;
